import Box from '@/common/components/Box'
import Button from '@/common/components/Button'
import { ErrorMessage } from '@/common/components/ErrorMessage'
import { MultipleDocumentUploader } from '@/common/components/MultipleDocumentUploader'
import SelectController from '@/common/components/SelectController'
import TextArea from '@/common/components/TextArea'
import TextField from '@/common/components/TextField'
import useBlobs from '@/common/hooks/queries/useBlobs'
import useDebounce from '@/common/hooks/useDebounce'
import { useGoBack } from '@/common/hooks/useGoBack'
import useUser from '@/common/hooks/useUser'
import { Mapper } from '@/common/services/mapper'
import { CompanyBranchData } from '@/common/types/companyBranches.type'
import { Option } from '@/common/types/option'
import { FormTransformers } from '@/common/utils/formTransformers'
import { useCompanies } from '@/features/companies/hooks/useCompanies'
import { CompaniesTransformer } from '@/features/companies/services/companiesTransformer'
import { useEstablishmentCompanies } from '@/features/establishments/hooks/useEstablishmentCompanies'
import { EstablishmentCompanyTransformer } from '@/features/establishments/services/establishmentCompanyTransformer'
import { useCreateExternalSupportTicket } from '@/features/support/external/hooks/useCreateExternalSupportTicket'
import { externalSupportTicketSchema, ExternalSupportTicketValidationSchema } from '@/features/support/external/schemas/create.schema'
import { useSupportReasons } from '@/features/support/hooks/useSupportReasons'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { ChevronLeftIcon } from 'lucide-react'
import { useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { branchTypeOptions } from '../../constants/companyKind.constant'
import { useAvailableCompanyBranches } from '@/features/privateCompanies/hooks/companyBranches/useAvailableCompanyBranches'
import { handlePastedImageUpload } from '@/common/utils/handlePastedImageUpload'

const ExternalSupportCreate = () => {
  const goBack = useGoBack()
  const queryClient = useQueryClient()

  const [baseError, setBaseError] = useState<string[]>([])

  const [searchSupportReason, setSearchSupportReason] = useDebounce('')
  const [searchCompany, setSearchCompany] = useDebounce('')
  const [searchBranch, setSearchBranch] = useDebounce('')

  const {
    register,
    handleSubmit,
    control,
    setError,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<ExternalSupportTicketValidationSchema>({
    resolver: zodResolver(externalSupportTicketSchema),
  })

  const { data: supportReasonOptions } = useSupportReasons({
    filters: { search: searchSupportReason },
    mapper: Mapper.mapToOptions({
      labelFieldName: 'display_name',
      valueFieldName: 'id',
    }),
  })

  const { data: establishmentOptions } = useEstablishmentCompanies({
    filters: { search: searchCompany },
    mapper: Mapper.mapWithTransformer({ transformer: EstablishmentCompanyTransformer.optionTransformer({ valueFieldName: 'matrix_branch_id' }) }),
  });

  const { data: clientCompaniesOptions } = useCompanies({
    filters: { search: searchCompany },
    mapper: Mapper.mapWithTransformer({ transformer: CompaniesTransformer.optionsTransformer() }),
  });

  const { data: clientBranchesOptions } = useAvailableCompanyBranches({
    companyId: watch('company.value'),
    filters: { search: searchBranch, },
    mapper: Mapper.mapToOptions<CompanyBranchData>({ valueFieldName: `id`, labelFieldName: `display_name` }),
  });

  const { data: user } = useUser({ id: 'me' })

  const { mutateAsync: uploadBlob, isPending: uploadBlobIsPending } = useBlobs()

  const { mutate: createSupportTicket, isPending: createSupportTicketIsPending } = useCreateExternalSupportTicket({
    creator_id: user?.id,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['externalSupportTickets'] })
      goBack({ fallback: '/external-support' })
    },
    onError: (error) => {
      if (error?.response?.data?.errors?.base?.length) {
        setBaseError(error.response.data.errors.base)
        return
      }

      FormTransformers.errorsTransformer<ExternalSupportTicketValidationSchema>({
        setError,
      })(error)
    },
  })

  const onSubmit: SubmitHandler<ExternalSupportTicketValidationSchema> = (data) => {
    createSupportTicket(data)
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="flex gap-4 items-center mb-6">
        <Button variant="link-white" onClick={() => goBack({ fallback: '/external-support' })}>
          <ChevronLeftIcon size={40} strokeWidth={1} />
        </Button>

        <h2 className="text-2xl font-bold">Abertura de chamado externo</h2>
      </div>

      <Box className="flex flex-col gap-4 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)] overflow-y-auto h-[calc(100vh-128px)]">
        {baseError?.map((error) => (
          <p className="text-sm text-danger-soft">{error}</p>
        ))}

        <SelectController
          control={control}
          name="reason"
          label="Motivo"
          markAsRequired
          errorMessage={errors.reason?.message}
          options={supportReasonOptions || []}
          onInputChange={setSearchSupportReason}
          isDisabled={createSupportTicketIsPending}
        />

        <TextField
          label="Assunto"
          markAsRequired
          errorMessage={errors.subject_line?.message}
          {...register('subject_line')}
          disabled={createSupportTicketIsPending}
        />

        <SelectController
          control={control}
          name="branch_type"
          className='w-full'
          label='Tipo de empresa'
          options={branchTypeOptions as Option[]}
          errorMessage={errors.branch_type?.message}
          triggerOnChange={(value) => {
            setValue('branch_type', value);
            setValue('company', null as never);
            setValue('branch', null as never);
          }}
        />

        <div className='flex gap-3'>
          <SelectController
            control={control}
            name="company"
            className='w-full'
            label={watch('branch_type')?.value === 'Establishment::CompanyBranch' ? 'Estabelecimento' : 'Empresa'}
            placeholder={watch('branch_type')?.value ? 'Selecione' : <span className='italic text-sm'>Selecione a o Tipo de empresa</span>}
            isDisabled={!watch('branch_type')?.value}
            options={watch('branch_type')?.value === 'Client::CompanyBranch' ? clientCompaniesOptions as Option[] : establishmentOptions}
            onInputChange={setSearchCompany}
            errorMessage={errors.company?.message}
            triggerOnChange={(value) => {
              setValue('company', value)
              setValue('branch', null as never);
            }}
          />

          {watch('branch_type')?.value === 'Client::CompanyBranch' && (
            <SelectController
              control={control}
              name="branch"
              label='Filial'
              className='w-full'
              placeholder={watch('company')?.value ? 'Selecione' : <span className='italic text-sm'>Selecione a Empresa</span>}
              isDisabled={!watch('company')?.value || !watch('branch_type')?.value}
              options={clientBranchesOptions as Option[]}
              onInputChange={setSearchBranch}
              errorMessage={errors.branch?.message}
            />
          )}
        </div>

        <TextArea
          rows={16}
          maxLength={2048}
          errorMessage={errors.content?.message}
          onPaste={(event) =>
            handlePastedImageUpload({
              event,
              uploadBlob,
              getValues,
              setValue,
              fieldName: 'documents'
            })
          }
          {...register('content')}
          disabled={createSupportTicketIsPending || uploadBlobIsPending}
        />

        <div>
          <MultipleDocumentUploader
            control={control}
            name="documents"
            uploadBlob={uploadBlob}
            fieldName="documents"
            accept=".png, .jpg, .jpeg, .webp, .pdf, .doc, .docx, .xls, .xlsx"
            isLoading={uploadBlobIsPending}
            setError={(message) => setError?.('documents', { message })}
            disabled={createSupportTicketIsPending || uploadBlobIsPending}
          />
          {errors.documents?.message && <ErrorMessage message={errors.documents?.message} />}
        </div>

        <Button
          name="submit"
          type="submit"
          className="self-end"
          disabled={createSupportTicketIsPending || uploadBlobIsPending}
          isLoading={createSupportTicketIsPending}
        >
          Enviar
        </Button>
      </Box>
    </form>
  )
}

export default ExternalSupportCreate
